import React, { ReactNode, useEffect, useRef, useState } from 'react';
import IMAGE_DEFAULT from 'images/broken_image_room.svg';
import { setPreventUnload } from 'flow/actions/app-actions';
import AudioPlayer from 'flow/components/tickets/convenience/AudioPlayer';
import Icon from 'flow/components/ui-components/Icon';
import IconButton from 'flow/components/ui-components/IconButton';
import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import { twMerge } from 'tailwind-merge';

const twohundredkb_in_bytes = 200 * 1024;

interface Props {
    fileName: string;
    url: string;
    previewUrl?: string;
    previewWidth?: number;
    previewHeight?: number;
    sentTime: any;
    fileSize?: number;
    fileType: string;
    // When false, the image is treated as a decorative/navigational icon
    // (e.g. an email social-media icon): no hover toolbar and no click-to-open.
    interactive?: boolean;
}

export const fileTypeIsAudio = (type: string) => type?.startsWith('audio/');
export const fileTypeIsImage = (type: string) => type?.startsWith('image/');
const fileTypeIsGif = (type: string) => type === 'image/gif';
export const fileTypeIsVideo = (type: string) => type?.startsWith('video/');

const MessageAttachment = ({
    fileName,
    fileSize,
    fileType,
    url,
    previewUrl,
    previewWidth,
    previewHeight,
    sentTime,
    interactive = true,
}: Props) => {
    if (fileTypeIsImage(fileType)) {
        return (
            <MessageAttachmentPreview
                fileName={fileName}
                fileSize={fileSize}
                isGif={fileTypeIsGif(fileType)}
                url={url}
                previewUrl={previewUrl}
                previewWidth={previewWidth}
                previewHeight={previewHeight}
                sentTime={sentTime}
                interactive={interactive}
            />
        );
    }

    if (fileTypeIsVideo(fileType)) return <VideoPlayer url={url} />;
    if (fileTypeIsAudio(fileType))
        return <AudioPlayer url={url} presentDownload={true} />;

    return (
        <MessageAttachmentFile
            fileName={fileName}
            fileType={fileType}
            url={url}
        />
    );
};

export default MessageAttachment;

interface FileProps {
    fileName: string;
    fileType: string;
    url: string;
}

const MessageAttachmentFile = ({ fileName, fileType, url }: FileProps) => {
    return (
        <Toolbar url={url}>
            <a
                href={url}
                download={true}
                rel="nofollow noopener noreferrer"
                target="_blank"
                className="flex w-auto max-w-full min-w-[200px] items-center gap-x-2 rounded-sm border border-gray-300 p-2"
                onClick={() => setPreventUnload(true)}
            >
                <Icon
                    iconKey={getFileTypeIcon(fileType)}
                    className="text-gray-600"
                />
                <span className="text-sm text-gray-800">{fileName}</span>
            </a>
        </Toolbar>
    );
};

export const IMAGE_HEIGHT_MAX = 200;

const getImageDimensionsAdjusted = (width?: number, height?: number) => {
    if (!height || height <= IMAGE_HEIGHT_MAX) return { width, height };
    const aspectRatio = width / height;
    return {
        width: Math.round(IMAGE_HEIGHT_MAX * aspectRatio),
        height: IMAGE_HEIGHT_MAX,
    };
};

const getFileTypeIcon = (type: string) => {
    if (fileTypeIsImage(type)) return 'image';
    if (fileTypeIsVideo(type)) return 'movie';
    return 'file';
};

// Utility to safely append fetchDate to image src
const getImageSrcWithFetchDate = (src: string, fetchDate: number) => {
    try {
        // Handles absolute and relative URLs
        const urlObj = src.startsWith('http')
            ? new URL(src)
            : new URL(src, window.location.origin);
        urlObj.searchParams.set('fetchDate', String(fetchDate));
        return urlObj.toString();
    } catch {
        // fallback for malformed or non-standard URLs
        return src + (src.includes('?') ? '&' : '?') + 'fetchDate=' + fetchDate;
    }
};

interface PreviewProps {
    fileName: string;
    fileSize?: number;
    url: string;
    previewUrl?: string;
    previewWidth?: number;
    previewHeight?: number;
    isGif?: boolean;
    sentTime: any;
    retryCountThreshold?: number;
    baseRetryDelay?: number;
    interactive?: boolean;
}
const MessageAttachmentPreview = ({
    fileName,
    fileSize,
    isGif,
    url,
    previewUrl,
    previewWidth,
    previewHeight,
    sentTime,
    retryCountThreshold = 5, // Maximum number of retry attempts before giving up
    baseRetryDelay = 2000, // Initial delay in ms, doubles with each retry (exponential backoff)
    interactive = true,
}: PreviewProps) => {
    const { openModal } = useModal();
    const imageRef = useRef<HTMLImageElement | null>(null);
    const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const retryCount = useRef(0);
    const [isLoaded, setIsLoaded] = useState(false);
    const [fetchDate, setFetchDate] = useState<number>(Date.now()); // Used to force reload

    // Reset fetchDate and retryCount when previewUrl or url changes
    useEffect(() => {
        setFetchDate(Date.now());
        retryCount.current = 0;
        setIsLoaded(false);
        // Ensure any pending retry timeout is cleared when image src changes
        if (retryTimeoutRef.current) {
            clearTimeout(retryTimeoutRef.current);
            retryTimeoutRef.current = null;
        }
    }, [previewUrl, url]);

    useEffect(() => {
        // Cleanup timeout on unmount
        return () => {
            if (retryTimeoutRef.current) {
                clearTimeout(retryTimeoutRef.current);
                retryTimeoutRef.current = null;
            }
        };
    }, []);

    if (!url) return <MediaUrlMissingItem />;
    if (!previewUrl && fileSize > twohundredkb_in_bytes) {
        return (
            <PreviewMissingItem
                url={url}
                fileName={fileName}
                sentTime={sentTime}
                fileSize={fileSize}
            />
        );
        // if there's no previewUrl, and fileSize is greater than 200 kb,
        // there's nothing to load.
    }

    /*
     * Retry Logic:
     * - retryCountThreshold: Maximum number of attempts before stopping retries.
     * - Exponential backoff: Delay doubles each retry, but is capped at 10s (10_000ms).
     * - If retryCountThreshold is reached, no further retries are attempted.
     */
    const onErrorLoadingPreviewUrl = () => {
        if (retryTimeoutRef.current) {
            clearTimeout(retryTimeoutRef.current);
            retryTimeoutRef.current = null;
        }
        setIsLoaded(false);

        if (!previewUrl) return;
        if (retryCount.current >= retryCountThreshold) {
            // Retry limit reached, stop retrying
            return;
        }
        retryCount.current += 1;

        // Exponential backoff for retry delay, capped at 10s
        const delay = Math.min(
            baseRetryDelay * Math.pow(2, retryCount.current - 1),
            10_000,
        );
        retryTimeoutRef.current = setTimeout(() => {
            setFetchDate(Date.now()); // Force reload by changing src
        }, delay);
    };

    // Clear timeout and reset retryCount when image loads successfully
    const onImageLoad = () => {
        setIsLoaded(true);
        retryCount.current = 0;
        if (retryTimeoutRef.current) {
            clearTimeout(retryTimeoutRef.current);
            retryTimeoutRef.current = null;
        }
    };

    const imageDimensions = getImageDimensionsAdjusted(
        previewWidth,
        previewHeight,
    );
    return (
        <Toolbar
            url={url}
            imageRef={imageRef}
            isGif={isGif}
            disabled={!isLoaded || !interactive}
        >
            <div className={!isLoaded ? 'relative' : undefined}>
                {!isLoaded ? (
                    <div
                        className={twMerge(
                            'animate-fade absolute inset-0 rounded-sm bg-gray-200',
                            !previewHeight
                                ? 'h-[200px] w-[200px] object-cover'
                                : '',
                        )}
                        style={{
                            height: imageDimensions.height,
                            width: imageDimensions.width,
                        }}
                    />
                ) : null}
                <img
                    ref={imageRef}
                    src={
                        retryCount.current === 0
                            ? previewUrl || url
                            : getImageSrcWithFetchDate(
                                  previewUrl || url,
                                  fetchDate,
                              )
                    }
                    className={twMerge(
                        'rounded-sm',
                        interactive ? 'cursor-pointer' : '',
                        !isLoaded ? 'opacity-0' : '',
                        !previewHeight
                            ? 'h-[200px] w-[200px] object-cover'
                            : '',
                    )}
                    height={imageDimensions.height}
                    width={imageDimensions.width}
                    alt={fileName}
                    onClick={() =>
                        interactive &&
                        openModal('imageTickets', {
                            fileName,
                            url,
                            sentTime,
                            fileSizeBytes: fileSize,
                        })
                    }
                    onLoad={onImageLoad}
                    onError={onErrorLoadingPreviewUrl}
                />
            </div>
        </Toolbar>
    );
};

const MediaUrlMissingItem = () => {
    const { i18next } = useAppContext();
    return (
        <div className="relative w-[200px]">
            <p className="absolute inset-x-0 mx-auto flex justify-center px-2 pt-4 text-center text-sm text-gray-400 italic">
                {i18next.t('chat.attachment.media_unavailable')}
            </p>
            <img
                src={IMAGE_DEFAULT}
                className="h-[200px] w-auto rounded-sm object-cover"
                alt=""
            />
        </div>
    );
};

interface PreviewMissingItemProps {
    url: string;
    fileName: string;
    sentTime: string;
    fileSize?: number;
}

const PreviewMissingItem = ({
    fileName,
    sentTime,
    url,
    fileSize,
}: PreviewMissingItemProps) => {
    const { i18next } = useAppContext();
    const { openModal } = useModal();

    return (
        <div className="relative w-[200px] cursor-pointer">
            <p className="absolute inset-x-0 top-6 mx-auto flex justify-center px-2 text-center text-sm text-gray-400 italic">
                {i18next.t('chat.attachment.preview_unavailable')}
            </p>
            <img
                src={IMAGE_DEFAULT}
                className="h-[200px] w-auto rounded-sm object-cover"
                alt=""
                onClick={() =>
                    openModal('imageTickets', {
                        fileName,
                        url,
                        sentTime,
                        fileSizeBytes: fileSize,
                    })
                }
            />
            <p className="absolute inset-x-0 bottom-6 mx-auto flex justify-center px-2 text-center text-sm text-gray-400 italic">
                {i18next.t('chat.attachment.media.click_to_load')}
            </p>
        </div>
    );
};

interface ToolbarProps {
    url: string;
    isGif?: boolean;
    imageRef?: React.MutableRefObject<HTMLImageElement | null>;
    children: ReactNode;
    disabled?: boolean;
}

const Toolbar = ({
    url,
    isGif,
    imageRef,
    children,
    disabled,
}: ToolbarProps) => {
    const { i18next } = useAppContext();
    const [toolbarVisible, setToolbarVisible] = useState(false);
    const [playing, setPlaying] = useState(true);

    if (disabled) return <>{children}</>;

    return (
        <div className="flex flex-row items-start">
            <div
                className="relative"
                onMouseEnter={() => setToolbarVisible(true)}
                onMouseLeave={() => setToolbarVisible(false)}
            >
                <div
                    className={twMerge(
                        'absolute -end-1.5 -top-1.5 flex flex-row gap-x-1 rounded-sm bg-white p-1 shadow-md',
                        toolbarVisible ? '' : 'hidden',
                    )}
                >
                    <a
                        href={url}
                        download={true}
                        rel="nofollow noopener noreferrer"
                        target="_blank"
                        onClick={() => {
                            setPreventUnload(true);
                        }}
                    >
                        <IconButton
                            tooltipLabel={i18next.t('Tooltip.Audio.Download')}
                        >
                            <Icon iconKey="download" />
                        </IconButton>
                    </a>
                    {isGif && playing && (
                        <IconButton
                            tooltipLabel={i18next.t('Tooltip.Title.Stop')}
                            onClick={() => {
                                setPlaying(false);
                                stopGifPlaying(imageRef);
                            }}
                        >
                            <Icon iconKey="stop" />
                        </IconButton>
                    )}
                    {isGif && !playing && (
                        <IconButton
                            onClick={() => {
                                setPlaying(true);
                                resumeGifPlaying(imageRef, url);
                            }}
                            tooltipLabel={i18next.t('Tooltip.Title.Start')}
                        >
                            <Icon iconKey="play_arrow" />
                        </IconButton>
                    )}
                </div>
                {children}
            </div>
        </div>
    );
};

//Copied from pages/messages/MessageAttachment.tsx
const stopGifPlaying = (
    imageRef: React.MutableRefObject<HTMLImageElement | null>,
) => {
    // There isn't any standard browser API to pause an animated gif, however by now the
    // Canvas API (including toDataURL) is very broadly available at the time of writing
    // (2024), so we can write the image into a canvas to snapshot it.
    const image = imageRef.current;
    if (!image) {
        return;
    }
    const canvas = document.createElement('canvas');
    canvas.width = image.width;
    canvas.height = image.height;
    canvas.getContext('2d').drawImage(image, 0, 0, image.width, image.height);
    image.src = canvas.toDataURL('image/gif');
};

//Copied from pages/messages/MessageAttachment.tsx
const resumeGifPlaying = (
    imageRef: React.MutableRefObject<HTMLImageElement | null>,
    originalUrl: string,
) => {
    // Restore original image.
    const image = imageRef.current;
    if (!image) {
        return;
    }
    image.src = originalUrl;
};

interface VideoPlayerProps {
    url: string;
}

const VideoPlayer = ({ url }: VideoPlayerProps) => {
    if (!url) return <MediaUrlMissingItem />;

    return (
        <video
            src={url}
            className="h-[200px] w-fit min-w-[200px] rounded-sm"
            controls={true}
            preload="metadata"
        />
    );
};
